import { Card } from 'components/ui/card';
import React from 'react';

export default function TermsOfService() {
  return (
    <Card maxWidth="none">
      <main className="flex justify-center">
        <article
          className="w-full p-3 text-sm leading-relaxed text-muted-foreground sm:p-6 sm:text-base"
          aria-labelledby="tos-title"
        >
          <header className="mb-6 space-y-3 md:mb-8">
            <div className="flex flex-wrap items-baseline gap-3">
              <h1 id="tos-title" className="text-2xl font-semibold tracking-tight text-foreground sm:text-4xl">
                Terms of Service
              </h1>
              <span className="rounded-full border px-2.5 py-1 text-xs text-gray-500 sm:text-sm">
                Effective: March 2026
              </span>
            </div>
            <p className="text-xs text-gray-600 sm:text-sm">
              Website:{' '}
              <a className="text-blue-600 underline-offset-2 hover:underline" href="https://wisefriend.org" rel="noopener">
                wisefriend.org
              </a>{' '}
              | Business: Wise Friend | Location: Nevada, United States
            </p>
          </header>

          <section className="space-y-6">
            <ol className="list-decimal space-y-6 pl-5 marker:font-semibold marker:text-foreground sm:pl-6">
              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Acceptance</h2>
                <p>
                  By using Wise Friend, you agree to these Terms and the{' '}
                  <a href="/privacy" className="text-blue-600 underline-offset-2 hover:underline">
                    Privacy Policy
                  </a>
                  .
                </p>
                <p>If you do not agree, do not use the platform.</p>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">What Wise Friend Is</h2>
                <p>
                  Wise Friend provides <strong>behavioral health skills training</strong>.
                </p>
                <p>It is:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>educational</li>
                  <li>skill-based</li>
                  <li>non-clinical</li>
                </ul>
                <p>It is not:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>therapy</li>
                  <li>medical care</li>
                  <li>mental health treatment</li>
                </ul>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Eligibility</h2>
                <p>You must be:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>at least 13 years old</li>
                  <li>able to form a legal agreement</li>
                </ul>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Your Account</h2>
                <p>You are responsible for:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>keeping login credentials secure</li>
                  <li>all activity under your account</li>
                </ul>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Programs &amp; Partner Organizations</h2>
                <p>Some users participate through organizations.</p>
                <p>By joining these programs:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>your activity and survey data may be shared with the sponsoring organization</li>
                  <li>participation may be tracked for program outcomes</li>
                </ul>
                <p>This is part of how the platform operates.</p>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Acceptable Use</h2>
                <p>You agree not to:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>misuse the platform</li>
                  <li>attempt to hack or disrupt systems</li>
                  <li>post harmful or illegal content</li>
                </ul>
                <p>We can suspend accounts if needed.</p>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">User Content</h2>
                <p>You own your content.</p>
                <p>You grant us a license to:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>store</li>
                  <li>process</li>
                  <li>display</li>
                </ul>
                <p>only to operate and improve the platform.</p>
                <p>We do not sell your private content.</p>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Mentorship &amp; Peer Support Disclaimer</h2>
                <p>Wise Friend includes mentorship and peer support features.</p>
                <p>You understand:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    mentors are <strong>not licensed therapists</strong>
                  </li>
                  <li>
                    guidance is <strong>not medical or clinical advice</strong>
                  </li>
                  <li>you are responsible for your own decisions</li>
                </ul>
                <p>If you are in crisis, you should seek professional help.</p>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">AI Features</h2>
                <p>Some features use AI.</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>outputs are generated automatically</li>
                  <li>may not always be accurate or appropriate</li>
                  <li>should not be relied on as professional advice</li>
                </ul>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Communications</h2>
                <p>
                  By creating an account, providing your email address and/or mobile phone number, and enabling
                  notification preferences, you consent to receive service-related communications from us by email
                  and SMS. These may include meeting reminders, cohort updates, administrative messages, and other
                  information related to your use of the service.
                </p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>SMS message frequency varies based on your activity and admin/cohort updates.</li>
                  <li>Message and data rates may apply.</li>
                  <li>You can opt out of SMS at any time by replying STOP. Reply HELP for help.</li>
                  <li>You can manage notification channels in your account settings.</li>
                </ul>
                <p>
                  We do not use these notifications for marketing or promotional campaigns unless you separately opt
                  in where required by law. Even if you disable optional notifications, we may still send essential
                  account and security communications.
                </p>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Disclaimer</h2>
                <p>The platform is provided &quot;as is.&quot;</p>
                <p>We do not guarantee:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>outcomes</li>
                  <li>accuracy</li>
                  <li>uninterrupted service</li>
                </ul>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Limitation of Liability</h2>
                <p>To the maximum extent allowed by law:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>we are not liable for indirect or consequential damages</li>
                  <li>total liability is limited to what you paid (if anything)</li>
                </ul>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Indemnification</h2>
                <p>You agree to indemnify Wise Friend against claims resulting from:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>your use of the platform</li>
                  <li>your content</li>
                  <li>violations of these terms</li>
                </ul>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Termination</h2>
                <p>We may suspend or terminate access at any time.</p>
                <p>You may stop using the platform at any time.</p>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Governing Law</h2>
                <p>Nevada law governs these Terms.</p>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Changes</h2>
                <p>We may update these Terms.</p>
                <p>Continued use = acceptance.</p>
              </li>

              <li className="space-y-2">
                <h2 className="text-base font-semibold text-foreground sm:text-xl">Contact</h2>
                <p>
                  <a href="mailto:support@wisefriend.org" className="text-blue-600 underline-offset-2 hover:underline">
                    support@wisefriend.org
                  </a>
                </p>
              </li>
            </ol>

            <hr />
            <p className="text-xs text-gray-500 sm:text-sm">(c) {new Date().getFullYear()} Wise Friend. All rights reserved.</p>
          </section>
        </article>
      </main>
    </Card>
  );
}
